import { Resource } from '@readium/shared';
import Decrypter from '../../../lib/Decrypter.js';
import fixXhtmlMarkup from './fixXhtmlMarkup.js';
import { sendBreadcrumb } from './sentryBreadcrumb.js';
import { dirFromPath, rebaseCssUrls } from './cssInliner.js';
import { rewriteFontUrlsInText } from './fontDeobfuscator.js';
import { adaptPublisherCssInHtml } from './publisherCssCompat.js';

/**
 * Wrapper for Resource that decrypts content when read.
 *
 * The readers it does not override, such as `readAsXML()`, come from Resource,
 * which builds them on `readAsString()` and so on the decrypted text.
 */
export class DecryptingResource extends Resource {
    constructor(originalResource, url, cssCache, idpfFontBlobUrls = null) {
        super();
        this.originalResource = originalResource;
        this.url = url;
        this.cssCache = cssCache;
        // [IDPF] Map<href, blobURL> for inline-style and external-CSS font rewrites.
        this.idpfFontBlobUrls = idpfFontBlobUrls;
    }

    _emitDecryptError(error) {
        sendBreadcrumb(
            'fetch',
            'decrypt_error',
            {
                href: this.url,
                errorName: error?.name,
                errorMessage: error?.message,
            },
            'error'
        );
    }

    async link() {
        return await this.originalResource.link();
    }

    async length() {
        return await this.originalResource.length();
    }

    async read(range) {
        return await this.originalResource.read(range);
    }

    async close() {
        return await this.originalResource.close();
    }

    async readAsString(charset) {
        try {
            const encryptedText = await this.originalResource.readAsString(charset);

            if (this.isTextFile()) {
                const decryptedText = await Decrypter.decryptText(encryptedText);
                if (
                    this.url.endsWith('.html') ||
                    this.url.endsWith('.xhtml') ||
                    this.url.endsWith('.htm') ||
                    this.url.endsWith('.xml') ||
                    this.url.endsWith('.ncx')
                ) {
                    let html;
                    try {
                        html = fixXhtmlMarkup(decryptedText);
                    } catch (xhtmlError) {
                        sendBreadcrumb(
                            'fetch',
                            'xhtml_fix_error',
                            {
                                href: this.url,
                                errorName: xhtmlError?.name,
                            },
                            'warning'
                        );
                        throw xhtmlError;
                    }

                    // Inline pre-decrypted CSS directly into the HTML so the navigator's
                    // DOMParser sees <style> tags instead of <link> tags. This avoids the
                    // browser trying (and failing) to fetch encrypted CSS from the server
                    // when the blob-URL iframe loads.
                    html = await this.inlineCssInHtml(html);

                    return html;
                }
                return decryptedText;
            }

            return encryptedText;
        } catch (error) {
            if (error?.status != null) {
                sendBreadcrumb(
                    'fetch',
                    'http_error',
                    {
                        href: this.url,
                        status: error.status,
                    },
                    'error'
                );
            } else {
                this._emitDecryptError(error);
            }
            console.warn('Failed to decrypt text content:', this.url, error);
            return await this.originalResource.readAsString(charset);
        }
    }

    /**
     * Replace <link rel="stylesheet"> tags in HTML with inline <style> tags
     * containing pre-decrypted CSS from the LazyCssCache. The CSS content is
     * XML-escaped (&, < and ]]>) so DOMParser can safely parse it as XHTML.
     *
     * Lazy: only the stylesheets actually referenced by this HTML are fetched.
     * The cache dedupes concurrent requests for the same href.
     */
    async inlineCssInHtml(html) {
        const xhtmlDir = dirFromPath(this.url);
        // Embedded <style> blocks and style attributes get the same publisher
        // CSS adaptation the cache applies to linked stylesheets. Done before
        // the links are inlined so the cached CSS is not scanned a second time.
        let result = adaptPublisherCssInHtml(html);

        if (this.cssCache && typeof this.cssCache.get === 'function') {
            // Also consume an optional explicit closing tag (`<link ...> </link>`,
            // valid XML some EPUBs use) — replacing only the opening tag would
            // leave an orphan </link> that breaks the navigator's strict
            // XHTML DOMParser ("Opening and ending tag mismatch").
            const linkRegex = /<link\b[^>]*>(?:\s*<\/link\s*>)?/gi;
            const matches = [...result.matchAll(linkRegex)];

            // Resolve all <link> hrefs in parallel; the cache dedupes.
            const replacements = await Promise.all(
                matches.map(async ([match]) => {
                    if (!/\brel\s*=\s*["']stylesheet["']/i.test(match)) return null;
                    const hrefMatch = match.match(/\bhref\s*=\s*["']([^"']+)["']/i);
                    if (!hrefMatch) return null;
                    const href = hrefMatch[1];
                    if (!href.endsWith('.css')) return null;

                    const cached = await this.cssCache.get(href, xhtmlDir);
                    if (!cached) return null;

                    const rebasedCss = rebaseCssUrls(cached.css, cached.cacheKey, xhtmlDir);

                    // `]]>` is not allowed in XML character data either: publisher CSS often
                    // carries the `/*<![CDATA[*/ ... /*]]>*/` idiom copied from a <style>
                    // block, and leaving it raw breaks the navigator's DOMParser.
                    const xmlSafeCss = rebasedCss
                        .replace(/&/g, '&amp;')
                        .replace(/</g, '&lt;')
                        .replace(/]]>/g, ']]&gt;');
                    return {
                        match,
                        replacement: `<style type="text/css" data-original-href="${href}">${xmlSafeCss}</style>`,
                    };
                })
            );

            for (const r of replacements) {
                if (r) result = result.replace(r.match, () => r.replacement);
            }
        }

        // [IDPF] Rewrite font url() references in inline <style> blocks of the XHTML.
        if (this.idpfFontBlobUrls && this.idpfFontBlobUrls.size > 0) {
            result = rewriteFontUrlsInText(result, this.idpfFontBlobUrls, xhtmlDir);
        }

        return result;
    }

    async readAsJSON() {
        try {
            const decryptedText = await this.readAsString();
            return JSON.parse(decryptedText);
        } catch (error) {
            this._emitDecryptError(error);
            console.warn('Failed to decrypt JSON content:', this.url, error);
            return await this.originalResource.readAsJSON();
        }
    }

    async readAsBlob() {
        try {
            if (this.isTextFile()) {
                const decryptedText = await this.readAsString();
                return new Blob([decryptedText], { type: this.getMimeType() });
            } else {
                const encryptedBlob = await this.originalResource.readAsBlob();
                const encryptedArrayBuffer = await encryptedBlob.arrayBuffer();
                const decryptedArrayBuffer = await Decrypter.decryptArrayBuffer(encryptedArrayBuffer);
                return new Blob([decryptedArrayBuffer], { type: this.getMimeType() });
            }
        } catch (error) {
            this._emitDecryptError(error);
            console.warn('Failed to decrypt blob content:', this.url, error);
            return await this.originalResource.readAsBlob();
        }
    }

    isTextFile() {
        return (
            this.url.endsWith('.html') ||
            this.url.endsWith('.htm') ||
            this.url.endsWith('.xhtml') ||
            this.url.endsWith('.xml') ||
            this.url.endsWith('.css') ||
            this.url.endsWith('.js')
        );
    }

    getMimeType() {
        if (this.url.endsWith('.html') || this.url.endsWith('.htm')) {
            return 'text/html';
        } else if (this.url.endsWith('.css')) {
            return 'text/css';
        } else if (this.url.endsWith('.js')) {
            return 'application/javascript';
        } else if (this.url.endsWith('.xhtml')) {
            return 'application/xhtml+xml';
        } else if (this.url.endsWith('.xml')) {
            return 'application/xml';
        } else if (this.url.endsWith('.json')) {
            return 'application/json';
        }
        return 'application/octet-stream';
    }
}
